/**
 * Formulário de um médico da agenda.
 *
 * Papel: preenche, valida e grava um médico — nome, especialidade, quem ele
 * atende, telefone, e-mail, clínica e observação —, servindo tanto à criação
 * quanto à edição.
 *
 * Como usar: quem monta este componente passa `medico` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por médico. Sem a `key`, o
 * React reaproveita a mesma instância ao trocar de registro e os campos
 * continuam com o que a pessoa via antes.
 *
 * Só o nome é obrigatório. Um médico de quem se tem apenas o nome e o telefone
 * precisa poder ser cadastrado sem que se invente especialidade nem endereço —
 * inventar é o que faz um cadastro deixar de ser confiável.
 */
import {
  type ContatoMedico,
  type NovoContatoMedico,
  novoContatoMedicoSchema,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { useSalvarMedico } from '../hooks/usar-saude';
import { MEDICO_EM_BRANCO, type MedicoNoFormulario } from '../types';

interface FormularioMedicoProps {
  /** O médico a editar, ou `null` para criar um novo. */
  medico: ContatoMedico | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<MedicoNoFormulario> | undefined> = {
  nome: 'nome',
  especialidade: 'especialidade',
  paciente: 'paciente',
  telefone: 'telefone',
  email: 'email',
  endereco: 'endereco',
  observacao: 'observacao',
};

/**
 * Converte o médico vindo da API no formato que o formulário guarda.
 *
 * @param medico - O médico a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais, com `null` trocado por string vazia.
 */
function valoresIniciais(medico: ContatoMedico | null): MedicoNoFormulario {
  if (medico === null) return { ...MEDICO_EM_BRANCO };

  return {
    nome: medico.nome,
    especialidade: medico.especialidade ?? '',
    paciente: medico.paciente ?? '',
    telefone: medico.telefone ?? '',
    email: medico.email ?? '',
    endereco: medico.endereco ?? '',
    observacao: medico.observacao ?? '',
  };
}

/**
 * O formulário de criação e de edição de médico.
 *
 * @param medico - O médico a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioMedico({ medico, onSalvo, onCancelar }: FormularioMedicoProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const salvar = useSalvarMedico();

  const form = useForm<MedicoNoFormulario, unknown, NovoContatoMedico>({
    resolver: zodResolver(novoContatoMedicoSchema),
    defaultValues: valoresIniciais(medico),
  });

  const { errors } = form.formState;
  const enviando = salvar.isPending;

  /** Grava o médico e leva o erro da API para o campo que o causou. */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({ id: medico?.id ?? null, dados });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="medico-nome">Nome</Label>
          <Input
            id="medico-nome"
            autoComplete="off"
            disabled={enviando}
            {...form.register('nome')}
          />
          {errors.nome && (
            <p className="text-sm text-destructive" role="alert">
              {errors.nome.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="medico-especialidade">Especialidade</Label>
          <Input
            id="medico-especialidade"
            autoComplete="off"
            disabled={enviando}
            {...form.register('especialidade')}
          />
          {errors.especialidade && (
            <p className="text-sm text-destructive" role="alert">
              {errors.especialidade.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="medico-paciente">Paciente(s)</Label>
          <Input
            id="medico-paciente"
            autoComplete="off"
            placeholder="Ex: Toda a família"
            disabled={enviando}
            {...form.register('paciente')}
          />
          {errors.paciente && (
            <p className="text-sm text-destructive" role="alert">
              {errors.paciente.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="medico-telefone">Telefone</Label>
          <Controller
            control={form.control}
            name="telefone"
            render={({ field }) => (
              <CampoMascarado
                id="medico-telefone"
                formato="telefone"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
              />
            )}
          />
          {errors.telefone && (
            <p className="text-sm text-destructive" role="alert">
              {errors.telefone.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="medico-email">E-mail</Label>
          <Input
            id="medico-email"
            type="email"
            autoComplete="off"
            disabled={enviando}
            {...form.register('email')}
          />
          {errors.email && (
            <p className="text-sm text-destructive" role="alert">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="medico-endereco">Clínica/Endereço</Label>
          <Input
            id="medico-endereco"
            autoComplete="off"
            placeholder="Nome da clínica, rua, número, bairro"
            disabled={enviando}
            {...form.register('endereco')}
          />
          {errors.endereco && (
            <p className="text-sm text-destructive" role="alert">
              {errors.endereco.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="medico-observacao">Observações</Label>
          <Textarea
            id="medico-observacao"
            rows={3}
            disabled={enviando}
            {...form.register('observacao')}
          />
          {errors.observacao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.observacao.message}
            </p>
          )}
        </div>

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar médico'}
        </Button>
      </DialogFooter>
    </form>
  );
}
